/**
 * Thanksgiving mug designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingMugDesignsPack: ToilePack = {
    slug: 'thanksgiving-mug-designs',
    category: 'season',
    name: 'Thanksgiving mug designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Grateful slogan', subject: 'The words "GRATEFUL" lettered in a rounded script with a small leaf, isolated'},
        {key: 'pie', label: 'Pie', subject: 'A slice of pumpkin pie with a cream dollop, isolated on a plain ground, no text'},
        {key: 'band', label: 'Pattern band', subject: 'A horizontal band of small leaves and acorns sized to wrap a mug, isolated, no text'},
    ],
    styles: [
        {key: 'harvest', label: 'Harvest flat', style: 'flat harvest illustration, simplified shapes, no outlines, matte colour', palette: 'Mustard, rust, pumpkin orange, olive and cream', framing: 'Centered on a solid cream background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage americana', style: 'vintage americana illustration, halftone grain, slight misregistration, warm', palette: 'Faded rust, brown, mustard and cream', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolour illustration, loose edges, paper texture showing', palette: 'Warm autumn washes of orange, ochre and brown', framing: 'Centered with white paper around it'},
    ],
    directions: ['a coffee-cup version instead', 'with a name added', 'as a wraparound border only'],
    directionHint: 'try: with a name added',
    seo: {
        title: 'Thanksgiving Mug Designs: Slogan, Pie & Band',
        metaDescription: 'Nine Thanksgiving mug wrap designs — Grateful slogan, pumpkin pie, leaf-and-acorn band — in harvest flat, vintage, and watercolor styles.',
        h1: 'Thanksgiving mug design: grateful slogan, pumpkin pie, pattern band',
        tagline: 'Nine designs, three graphics, three treatments each.',
        intro: 'Nine flat artwork designs built to wrap a mug, shown isolated with no mug in frame: a Grateful slogan with a small leaf, a slice of pumpkin pie, and a repeating band of leaves and acorns, each in a harvest flat, a vintage americana, and a watercolor treatment. Ready to apply to any mug template or print service.',
        useCases: [
            'a print-on-demand mug listing',
            'a sublimation mug wrap at home',
            'a gift shop or craft fair mug',
            'a matching coaster or napkin set',
        ],
        faq: [
            {q: 'Does the design already show it applied to a mug?', a: 'No — each file is the flat artwork alone, isolated on a plain background, so you can apply it to any mug mockup, wrap template, or print service yourself.'},
            {q: 'Is the pattern band sized to wrap all the way around a mug?', a: 'Yes — the band design is composed as a horizontal repeating strip, proportioned for a standard mug wrap.'},
            {q: "What's the difference between the three styles?", a: 'Harvest flat uses simplified matte shapes, vintage americana adds halftone grain and warm faded color, and watercolor uses soft loose edges with visible paper texture.'},
            {q: 'Can I use these designs for a mug I sell commercially?', a: 'Yes — the designs are cleared for commercial use, including print-on-demand and craft-fair sales.'},
        ],
    },
}
